import { Component, EventEmitter, input, OnInit, output, Output, signal, Signal, WritableSignal, computed, effect } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DataTableComponent, TableColumn } from '@libs/front/components/data-table/data-table.component';
import { ButtonListComponent } from '@libs/front/components/button-list/button-list.component';
import { Blast, Hole, HoleStatus, HoleStatusHistoryUtil } from 'src/app/services/hole.service';
import { FormsModule } from '@angular/forms';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ImportModalComponent } from '../../import-modal/import-modal.component';

@Component({
  selector: 'app-holes-list-data',
  standalone: true,
  imports: [DataTableComponent, ButtonListComponent, TranslateModule, FormsModule],
  templateUrl: './holes-list-data.component.html',
  styleUrls: ['./holes-list-data.component.scss']
})
export class HolesListDataComponent{
  holes = input<Hole[]>([]);
  blast = input<Blast | null>(null);
  
  selectedHoleId = input<string | null>(null);
    holeSelected = output<{
    holeId: string | null;
    changeCollapsed: boolean;
  }>();
  currentFilter = signal<string | { columnKey: string; searchTerm: string } | undefined>(undefined);
  searchTerm = '';
  holeName = '';
  highlightedHoleName = signal<string | null>(null);

  holesData: WritableSignal<any[]> = signal([]);
  private previousSelectedHoleId: string | null = null;

  // Table columns configuration
  readonly allColumns: TableColumn[] = [
    { key: 'name', label: 'QAQC_TAB.HOLE' },
    { key: 'status', label: 'QAQC_TAB.STATUS' },
    { key: 'head_XY', label: 'QAQC_TAB.HEAD_XY' },
    { key: 'toe_XY', label: 'QAQC_TAB.TOE_XY' },
    { key: 'lengthPlanned', label: 'QAQC_TAB.LENGTH_PLANNED' },
    { key: 'lengthDrilled', label: 'QAQC_TAB.LENGTH_DRILLED' },
    { key: 'minedDrilled', label: 'QAQC_TAB.MINED_DRILLED' },
    { key: 'toeLevelPlanned', label: 'QAQC_TAB.TOE_LEVEL_PLANNED' },
    { key: 'toeLevelMesured', label: 'QAQC_TAB.TOE_LEVEL_MEASURED' },
    { key: 'diameterPlanned', label: 'QAQC_TAB.DIAMETER_PLANNED' },
    { key: 'diameterMeasured', label: 'QAQC_TAB.DIAMETER_MEASURED' },
    { key: 'hasSurveyed', label: 'QAQC_TAB.HAS_SURVEYED' },
    { key: 'hasWater', label: 'QAQC_TAB.WATER' },
    { key: 'hasTube', label: 'QAQC_TAB.TUBE' },
    { key: 'comments', label: 'QAQC_TAB.COMMENT' }
  ];

  visibleColumnKeys: WritableSignal<Set<string>> = signal(new Set(this.allColumns.map(c => c.key)));

  visibleColumns = computed(() =>
    this.allColumns.filter(c => this.visibleColumnKeys().has(c.key))
  );

  importModal: BsModalRef;

  readonly addHole = output<void>();

  constructor(private decimalPipe: DecimalPipe, private readonly modalService: BsModalService) {
    // Utiliser effect() pour détecter les changements de selectedHoleId
    effect(() => {
      const currentHoleId = this.selectedHoleId();
      
      const foundHoleName = this.holes().find(h => h.publicGuid === currentHoleId)?.name ?? null;
      this.holeName = foundHoleName ?? '';
      this.highlightedHoleName.set(foundHoleName);
      
      this.previousSelectedHoleId = currentHoleId;
    });

    // Effect pour mettre à jour holesData quand holes() change
    effect(() => {
      const currentHoles = this.holes();
      this.updateHolesData(currentHoles);
    });
  }



    private updateHolesData(holes: Hole[]): void {
        const newData = holes.map(hole => {
            const lengthPlanned = hole.Geometry.Design?.length;
            const lengthDrilled = hole.Geometry.drilllength != null
                                    ? hole.Geometry.drilllength
                                    : hole.Geometry.Measure?.length;
            return {
                name: hole.name,
                status: this.getHoleStatusHistory(hole).join(', '),
                head_XY: this.decimalPipe.transform(hole.calculatedValues?.headPositionMagnitude, '1.2-2'),
                toe_XY: this.decimalPipe.transform(hole.calculatedValues?.toePositionMagnitude, '1.2-2'),
                lengthPlanned: this.decimalPipe.transform(lengthPlanned, '1.2-2'),
                lengthDrilled: this.decimalPipe.transform(lengthDrilled, '1.2-2'),
                minedDrilled: (hole.Geometry.Measure?.length && hole.Geometry.Measure.length !== 0)
                  ? this.decimalPipe.transform(hole.Geometry.Measure.length, '1.2-2')
                  : null,
                toeLevelPlanned: this.decimalPipe.transform(this.getToeLevelData(hole).planned, '1.2-2'),
                toeLevelMesured: this.decimalPipe.transform(this.getToeLevelData(hole).measured, '1.2-2'),
                diameterPlanned: this.decimalPipe.transform((hole.Geometry.Design?.diameter * 1000), '1.2-2'),
                diameterMeasured: hole.Geometry.Measure?.diameter ? this.decimalPipe.transform((hole.Geometry.Measure?.diameter * 1000), '1.2-2') : null,
                hasSurveyed: hole.Geometry.surveyed,
                hasWater: hole.hasWater,
                hasTube: hole.hasTube,
                comments: hole.comments
            };
        });
        this.holesData.set(newData);
    }

    getHoleStatusHistory(hole: Hole): HoleStatus[] {
      if (hole.statusHistory !== undefined) {
        return HoleStatusHistoryUtil.getStatusHistory(hole.statusHistory);
      }
      return [];
    }

    getToeLevelData(hole: Hole): { planned: number | null, measured: number | null, difference: number | null } {
    if (hole.calculatedValues?.toeLevelData) {
      const data = hole.calculatedValues.toeLevelData;
      return {
        planned: data.planned,
        measured: data.measured,
        difference: data.difference
      };
    }
    
    return {
      planned: null,
      measured: null,
      difference: null
    };
  }

  onHoleSelected(hole: any): void {
    const holeId = this.holes().find(h => h.name === hole.name)?.publicGuid || null;
    this.holeSelected.emit({ holeId: holeId, changeCollapsed: true });
  }

  onSearchChange() {
    this.currentFilter.set({ 
        columnKey: 'name', 
        searchTerm: this.searchTerm
      });
  }

  onAddHole() {
    var initialState = {
      uploadUrl: 'Blasts/' + this.blast()?.tirId + '/blastFromStr',
      allowStrFileImport: true
    };
    this.importModal = this.modalService.show(ImportModalComponent, { backdrop: true, ignoreBackdropClick: true, keyboard: false, initialState, class: 'modal-dialog-centered' });
    const subscription = this.modalService.onHide.subscribe({
      next: () => {
        this.addHole.emit();
        subscription.unsubscribe();
      },
      error: (error) => {
        console.error('Error with modal subscription:', error);
      },
      complete: () => {
        subscription.unsubscribe();
      }
    });
  }
    
}
